// Blog Archive: category filter + "Learn more" pagination via live REST
// fetches against /wp-json/wp/v2/posts. First 3 posts render as cards, the rest as list rows.

const CLOCK_SVG =
  '<svg class="klik-clock-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/>' +
  '<path d="M12 7.5V12l3 1.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Matches klik_get_reading_time()'s PHP formula (ceil, 200wpm) so reading
// time stays consistent between server-rendered and client-fetched pages.
function readingTime(html) {
  const text = (html || '').replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

// Safely turn a REST `*.rendered` HTML string into plain text.
function decodeText(html) {
  const t = document.createElement('textarea');
  t.innerHTML = (html || '').replace(/<[^>]+>/g, '');
  return t.value;
}

function pickCategory(post) {
  const terms = post._embedded && post._embedded['wp:term'];
  if (!terms) return '';
  for (const group of terms) {
    if (group && group.length && group[0].name) return group[0].name;
  }
  return '';
}

function rowMarkup(post) {
  const cat = pickCategory(post);
  const minutes = readingTime(post.content && post.content.rendered);
  const title = decodeText(post.title && post.title.rendered);
  const link = post.link || '#';
  const catPart = cat ? '<span class="section-blog-list__cat">' + cat + '</span>' : '';

  return (
    '<article class="section-blog-list__item">' +
    '<div class="section-blog-list__meta">' + catPart +
    '<span class="section-blog-list__time">' + CLOCK_SVG + minutes + ' min to read</span></div>' +
    '<h3 class="section-blog-list__title"><a class="section-blog-list__link" href="' + link + '">' + title + '</a></h3>' +
    '</article>'
  );
}

// Featured-image URL for a REST post fetched with `_embed` (matches the
// 'large' size PHP's klik_render_blog_card() requests via get_the_post_thumbnail_url()).
function pickImage(post) {
  const media = post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0];
  if (!media || media.code) return '';
  if (media.media_details && media.media_details.sizes && media.media_details.sizes.large) {
    return media.media_details.sizes.large.source_url;
  }
  return media.source_url || '';
}

function cardMarkup(post) {
  const cat = pickCategory(post);
  const minutes = readingTime(post.content && post.content.rendered);
  const title = decodeText(post.title && post.title.rendered);
  const link = post.link || '#';
  const img = pickImage(post);
  const catPart = cat ? '<span class="section-blog-cards__cat">' + cat + '</span>' : '';
  const imgPart = img ? '<img src="' + img + '" alt="" loading="lazy" />' : '';

  return (
    '<article class="section-blog-cards__card">' +
    '<a class="section-blog-cards__img" href="' + link + '" aria-hidden="true" tabindex="-1">' + imgPart + '</a>' +
    '<div class="section-blog-cards__meta">' + catPart +
    '<span class="section-blog-cards__time">' + CLOCK_SVG + minutes + ' min to read</span></div>' +
    '<h3 class="section-blog-cards__title"><a class="section-blog-cards__link" href="' + link + '">' + title + '</a></h3>' +
    '</article>'
  );
}

export default function initBlogArchive() {
  const root = document.querySelector('.section-blog-archive');
  if (!root) return;

  const listEl = root.querySelector('.section-blog-archive__list');
  const cardsEl = root.querySelector('.section-blog-archive__cards');
  const moreWrap = root.querySelector('.section-blog-archive__more-wrap');
  const moreBtn = root.querySelector('.section-blog-archive__more');
  const tags = Array.from(root.querySelectorAll('.section-blog-archive__filter-tag'));
  if (!listEl) return;

  const perPage = parseInt(root.dataset.perPage, 10) || 5;
  let totalPages = parseInt(root.dataset.totalPages, 10) || 1;
  let catId = '';
  let page = 1;
  let loading = false;

  function updateMoreVisibility() {
    if (!moreWrap) return;
    moreWrap.style.display = page >= totalPages ? 'none' : '';
  }

  function fetchPosts(targetPage, append) {
    if (loading) return;
    loading = true;
    if (moreBtn) moreBtn.disabled = true;

    let url = '/wp-json/wp/v2/posts?_embed&per_page=' + perPage + '&page=' + targetPage;
    if (catId) url += '&categories=' + catId;

    fetch(url)
      .then((r) => {
        totalPages = parseInt(r.headers.get('X-WP-TotalPages'), 10) || 1;
        return r.json();
      })
      .then((posts) => {
        if (!Array.isArray(posts)) throw new Error('Unexpected REST response');

        if (append) {
          // "Learn more" pages only ever add list rows — cards are a
          // page-1-only, non-repeating feature.
          if (posts.length) {
            listEl.insertAdjacentHTML('beforeend', posts.map(rowMarkup).join(''));
          }
        } else {
          const cardPosts = posts.slice(0, 3);
          const rowPosts = posts.slice(3);

          if (cardsEl) {
            cardsEl.innerHTML = cardPosts.map(cardMarkup).join('');
            cardsEl.style.display = cardPosts.length ? '' : 'none';
          }

          listEl.innerHTML = rowPosts.length
            ? rowPosts.map(rowMarkup).join('')
            : posts.length
              ? ''
              : '<p class="section-blog-archive__empty">Nothing to show yet.</p>';
        }

        page = targetPage;
        updateMoreVisibility();
      })
      .catch(() => {
        if (!append) {
          if (cardsEl) {
            cardsEl.innerHTML = '';
            cardsEl.style.display = 'none';
          }
          listEl.innerHTML = '<p class="section-blog-archive__empty">Couldn’t load posts right now.</p>';
        }
      })
      .finally(() => {
        loading = false;
        if (moreBtn) moreBtn.disabled = false;
      });
  }

  tags.forEach((tag) => {
    tag.addEventListener('click', () => {
      if (tag.classList.contains('is-active')) return;
      tags.forEach((t) => t.classList.remove('is-active'));
      tag.classList.add('is-active');
      catId = tag.dataset.catId || '';
      totalPages = 1; // corrected as soon as the response header comes back
      fetchPosts(1, false);
    });
  });

  if (moreBtn) {
    moreBtn.addEventListener('click', () => fetchPosts(page + 1, true));
  }

  updateMoreVisibility();

  // Cosmetic-only: append the arrow glyph to the newsletter's Subscribe
  // button without touching the CF7 form template/fields.
  const submitBtn = root.querySelector('.section-blog-archive__newsletter input.wpcf7-submit');
  if (submitBtn && !/→\s*$/.test(submitBtn.value)) {
    submitBtn.value = submitBtn.value.replace(/\s*$/, '') + ' →';
  }

  // "Successfully subscribed." popup shown after a CF7 submit on the newsletter form above
  // (Figma 663-5685). Scoped via the event target's ancestor, as in web-studio.js.
  const popup = document.getElementById('klikSubscribePopup');
  if (popup) {
    const openPopup = () => {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const closePopup = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.addEventListener('wpcf7mailsent', (event) => {
      if (!event.target.closest('.section-blog-archive__newsletter-form')) return;
      openPopup();
    });

    popup.querySelectorAll('[data-popup-close]').forEach((el) => {
      el.addEventListener('click', closePopup);
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });
  }
}
